import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { RiHospitalFill, } from 'react-icons/ri';
import { Badge } from '@heroui/react';

import {
  FaWrench,
  FaSignOutAlt,
  FaUser,
  FaUsers,
  FaTimes,
  FaHome,
  FaEdit,
  FaBook,
  FaClipboardList,
  FaFlask,
  FaChevronDown,
  FaChevronUp,
  FaShapes,
  FaChartLine,
  FaTint,
  FaSun,
  FaFolderOpen,
} from 'react-icons/fa';
import { UserContext } from '../context/UserContext';
import './Sidebar.scss';
import waveGif from '../assets/onboarding/wave.gif';
import preemieNutritionIcon from '../assets/apps/preemie-nutrition-app.svg';
import testTubesIcon from '../assets/apps/test-tubes-app.svg';
import protocolsIcon from '../assets/apps/protocols-app.svg';
import resusIcon from '../assets/apps/resus-app.svg';

const Sidebar = ({ isExpanded = window.innerWidth >= 1024, onToggle = () => { } }) => {
  const { user, hospitals, permittedApps, pendingRequests, outdatedProtocols, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenuItem, setActiveMenuItem] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [appsSubmenuOpen, setAppsSubmenuOpen] = useState(false);
  const [gifSrc, setGifSrc] = useState(waveGif);
  const gifRef = useRef(null);
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      onToggle(!mobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onToggle]);

  useEffect(() => {
    setActiveMenuItem(location.pathname + location.search);

    // Check if current path is under apps submenu and open it
    const appsRelatedPaths = [
      '/apps',
      '/resusAdmin',
      '/resusAdmin/medications',
      '/training',
      '/test-tubes',
      '/preemie-nutrition-config',
      '/nicu-nutrition-config',
      '/new-preemie-config',
      '/sepsis-screening-config',
      '/hematological-diseases-config',
      '/phototherapy-config',
      '/bili2022-config',
      '/antibiogram',
      '/settings',
      '/apps-mgmt',
      '/stats'
    ];
    if (appsRelatedPaths.some(path => location.pathname.startsWith(path))) {
      setAppsSubmenuOpen(true);
    }

  }, [location, isMobile]);

  // Stop GIF animation after 5 seconds for non-logged users
  useEffect(() => {
    if (!user) {
      const timer = setTimeout(() => {
        // Replace with static image or create a canvas to freeze current frame
        if (gifRef.current) {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = gifRef.current.naturalWidth;
          canvas.height = gifRef.current.naturalHeight;
          ctx.drawImage(gifRef.current, 0, 0);
          const staticImageData = canvas.toDataURL('image/png');
          setGifSrc(staticImageData);
        }
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    if (isMobile) {
      onToggle(false);
    }
    navigate('/login');
  };

  const handleMenuItemClick = () => {
    if (isMobile) {
      onToggle(false);
    }
  };

  const toggleAppsSubmenu = (e) => {
    e.preventDefault();
    setAppsSubmenuOpen(!appsSubmenuOpen);
  };

  const getHomepageDepartments = () => {
    if (user?.isSuperAdmin || !hospitals) {
      return [];
    }

    const homepageDepts = [];
    hospitals.forEach(hospital => {
      hospital.departments.forEach(department => {
        if (department.homepage) {
          homepageDepts.push(department);
        }
      });
    });

    return homepageDepts;
  };

  const hasTestTubesPermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'TEST_TUBES');
  };

  const hasPreemieNutritionPermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'PREEMIE_NUTRITION');
  };

  const hasNicuNutritionPermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'NICU_NUTRITION');
  };

  const hasNewPreemiePermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'NEW_PREEMIE');
  };

  const hasSepsisAppPermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'SEPSIS_SCREENING');
  };

  const hasAntibiogramPermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'ANTIBIOGRAM');
  };

  const hasHematologicalDiseasesPermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'HEMATOLOGICAL_DISEASES');
  };

  const hasPhototherapyPermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'PHOTOTHERAPY');
  };

  const hasBili2022Permissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'BILI2022');
  };

  const hasResusPermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'RESUS');
  };

  const hasTrainingPermissions = () => {
    if (user?.isSuperAdmin) return true;

    return permittedApps.some(app => app.key === 'TRAINING');
  };

  if (!user) {
    return (
      <div className="sidebar-container">
        <div className={`sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="sidebar-content">
            <div className="welcome-content">
              <div className="welcome-gif-container">
                <img
                  ref={gifRef}
                  src={gifSrc}
                  alt="Welcome animation"
                  className="welcome-gif"
                />
              </div>
              <div className="welcome-text">
                <p className="existing-user">
                  משתמש קיים? <Link to="/login" state={{ from: location }} className="welcome-link">ברוך בואך</Link>
                </p>
                <p className="new-user">
                  משתמש חדש? <Link to="/onboarding" className="welcome-link">נעים להכיר</Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Close button for mobile */}
        <button
          className={`close-btn ${isExpanded ? 'visible' : ''}`}
          onClick={() => onToggle(false)}
        >
          <FaTimes />
        </button>
      </div>
    );
  }

  const canShowAppsMenu = () => {
    return user.isSuperAdmin || hasResusPermissions() || hasPreemieNutritionPermissions() || hasNicuNutritionPermissions() || hasTestTubesPermissions() || hasTrainingPermissions() || hasNewPreemiePermissions() || hasAntibiogramPermissions() || hasSepsisAppPermissions() || hasHematologicalDiseasesPermissions() || hasPhototherapyPermissions() || hasBili2022Permissions();
  };

  const getAppsDestination = () => {
    if (!hospitals || hospitals.length === 0 || user.isSuperAdmin) {
      return '/apps';
    }

    if (
      hospitals.some(
        (hospital) =>
          hospital.nameEnglish.toLowerCase() === 'emekid'
      )
    ) {
      return '/emek';
    }
    return "/" + hospitals[0].nameEnglish.toLowerCase();
  };

  const menuItemClass = (path) =>
    `menu-item ${activeMenuItem.startsWith(path) ? 'active' : ''}`;

  const isSubmenuItemActive = () => {
    const appsRelatedPaths = [
      '/apps',
      '/resusAdmin',
      '/resusAdmin/medications',
      '/training',
      '/test-tubes',
      '/preemie-nutrition-config',
      '/nicu-nutrition-config',
      '/new-preemie-config',
      '/sepsis-screening-config',
      '/hematological-diseases-config',
      '/phototherapy-config',
      '/bili2022-config',
      '/antibiogram',
      '/settings',
      '/apps-mgmt',
      '/stats'
    ];
    return appsRelatedPaths.some(path => location.pathname.startsWith(path));
  };

  const getPendingRequestsCount = () => {
    if (!user || !pendingRequests) return 0;

    if (user.isSuperAdmin) {
      // Super admin can see all pending requests
      return pendingRequests.filter(req => req.type === 'ask-to-join-to-department').length;
    }

    // Regular admin can only see requests for their departments
    const adminDepartments = user.roles
      .filter(role => role.role === 'ADMIN')
      .map(role => role.department);

    return pendingRequests.filter(req =>
      req.type === 'ask-to-join-to-department' &&
      adminDepartments.includes(req.department?._id || req.department)
    ).length;
  };

  return (
    <div className="sidebar-container">
      <div className={`sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-content">
          {/* Main Navigation */}
          <nav className="main-nav">
            <ul>
              <li className={menuItemClass('/homepage')}>
                <Link
                  to="/homepage"
                  className="full-width-link"
                  onClick={handleMenuItemClick}
                  data-track-id="nav_homepage"
                >
                  <FaHome style={{ marginRight: '10px' }} />
                  דף הבית
                </Link>
              </li>
              <li className="divider"></li>

              <li className={menuItemClass('/protocols')}>
                <Link
                  to="/protocols"
                  className="full-width-link"
                  onClick={handleMenuItemClick}
                  data-track-id="nav_protocols"
                >
                  <div style={{ marginRight: '10px', display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}>
                    {outdatedProtocols && outdatedProtocols.length > 0 ? (
                      <Badge
                        content={outdatedProtocols.length}
                        color="danger"
                        size="sm"
                        placement="top-right"
                      >
                        <img src={protocolsIcon} alt="protocols" style={{ width: '16px', height: '16px', display: 'block' }} />
                      </Badge>
                    ) : (
                      <img src={protocolsIcon} alt="protocols" style={{ width: '16px', height: '16px', display: 'block' }} />
                    )}
                  </div>
                  <span>פרוטוקולים</span>
                </Link>
              </li>
              <li className="divider"></li>
              <li className={menuItemClass('/department-documents')}>
                <Link
                  to="/department-documents"
                  className="full-width-link"
                  onClick={handleMenuItemClick}
                  data-track-id="nav_department_documents"
                >
                  <FaFolderOpen style={{ marginRight: '10px' }} />
                  מסמכי המחלקה
                </Link>
              </li>
              <li className="divider"></li>
              <li className={menuItemClass('/main/')}>
                <Link
                  to="/main/apps"
                  className="full-width-link"
                  onClick={handleMenuItemClick}
                  data-track-id="nav_apps"
                >
                  <FaShapes style={{ marginRight: '10px' }} />
                  אפליקציות
                </Link>
              </li>
              <li className="divider"></li>
              <li className={menuItemClass('/cases')}>
                <Link
                  to="/cases"
                  className="full-width-link"
                  onClick={handleMenuItemClick}
                  data-track-id="nav_cases"
                >
                  <FaBook style={{ marginRight: '10px' }} />
                  מקרים רפואיים
                </Link>
              </li>
              <li className="divider"></li>
                <li className={menuItemClass('/checklists')}>
                  <Link
                    to="/checklists"
                    className="full-width-link"
                    onClick={handleMenuItemClick}
                    data-track-id="nav_checklists"
                  >
                    <FaClipboardList style={{ marginRight: '10px' }} />
                    רשימות
                  </Link>
                </li>
                <li className="divider"></li>

              {(user.isSuperAdmin ||
                user.roles.some((role) => role.role === 'ADMIN')) && (
                  <>
                    <li className={menuItemClass('/users')}>
                      <Link
                        to="/users"
                        className="full-width-link"
                        onClick={handleMenuItemClick}
                        data-track-id="nav_users"
                      >
                        <div style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}>
                          {getPendingRequestsCount() > 0 ? (
                            <Badge
                              content={getPendingRequestsCount()}
                              color="danger"
                              size="sm"
                              placement="top-right"
                            >
                              <FaUsers />
                            </Badge>
                          ) : (
                            <FaUsers />
                          )}
                        </div>
                        <span>משתמשים</span>
                      </Link>
                    </li>
                    <li className="divider"></li>
                  </>
                )}

              {user.isSuperAdmin && (
                <>
                  <li className={menuItemClass('/hospitals')}>
                    <Link
                      to="/hospitals"
                      className="full-width-link"
                      onClick={handleMenuItemClick}
                      data-track-id="nav_hospitals"
                    >
                      <RiHospitalFill style={{ marginRight: '10px' }} />
                      בתי חולים
                    </Link>
                  </li>
                  <li className="divider"></li>
                </>
              )}
                            {/* Apps Parent Menu Item - Only show if user has required permissions */}
              {canShowAppsMenu() && (
                <>
                  <li className="collapsible-menu-item">
                    <a href="#" className="full-width-link" onClick={toggleAppsSubmenu}>
                      <FaShapes style={{ marginRight: '10px' }} />
                      ניהול אפליקציות
                      {appsSubmenuOpen ? (
                        <FaChevronUp style={{ marginRight: 'auto' }} />
                      ) : (
                        <FaChevronDown style={{ marginRight: 'auto' }} />
                      )}
                    </a>
                  </li>

                  {/* Apps Submenu */}
                  <div className={`submenu-container ${appsSubmenuOpen ? 'expanded' : 'collapsed'}`}>
                    <ul className="submenu-list">
                      {user.isSuperAdmin && (
                        <li className={`submenu-item ${menuItemClass('/resusAdmin')}`}>
                          <Link
                            to="/resusAdmin"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                            data-track-id="nav_resus_admin"
                          >
                            <img src={resusIcon} alt="resus" style={{ width: '16px', height: '16px', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }} />
                            חדר הלם
                          </Link>
                        </li>
                      )}
                      {hasResusPermissions() && (
                        <li className={`submenu-item ${menuItemClass('/resusAdmin/medications')}`}>
                          <Link
                            to="/resusAdmin/medications"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                            data-track-id="nav_resus_medications_admin"
                          >
                            <img src={resusIcon} alt="resus" style={{ width: '16px', height: '16px', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }} />
                            מאגר תרופות
                          </Link>
                        </li>
                      )}

                      {hasTrainingPermissions() && (
                        <li className={`submenu-item ${menuItemClass('/training')}`}>
                          <Link
                            to="/training"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                            data-track-id="nav_training"
                          >
                            <FaEdit style={{ marginRight: '10px' }} />
                            אימון ובחינה
                          </Link>
                        </li>
                      )}

                      {/* Test tubes with updated permissions */}
                      {hasTestTubesPermissions() && (
                        <li className={`submenu-item ${menuItemClass('/test-tubes')}`}>
                          <Link
                            to="/test-tubes"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                            data-track-id="nav_test_tubes"
                          >
                            <img src={testTubesIcon} alt="test tubes" style={{ width: '16px', height: '16px', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }} />
                            מבחנות מעבדה
                          </Link>
                        </li>
                      )}
                      {hasPreemieNutritionPermissions() && (
                           <li className={`submenu-item ${menuItemClass('/preemie-nutrition-config')}`}>
                             <Link
                               to="/preemie-nutrition-config"
                               className="full-width-link"
                               onClick={handleMenuItemClick}
                               data-track-id="nav_preemie_nutrition"
                             >
                               <img src={preemieNutritionIcon} alt="preemie nutrition" style={{ width: '16px', height: '16px', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }} />
                               תזונת פגים
                             </Link>
                           </li>
                         )}
                      {hasNicuNutritionPermissions() && (
                           <li className={`submenu-item ${menuItemClass('/nicu-nutrition-config')}`}>
                             <Link
                               to="/nicu-nutrition-config"
                               className="full-width-link"
                               onClick={handleMenuItemClick}
                               data-track-id="nav_nicu_nutrition"
                             >
                               <img src={preemieNutritionIcon} alt="nicu nutrition" style={{ width: '16px', height: '16px', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }} />
                               הזנה בטיפול נמרץ ילדים
                             </Link>
                           </li>
                         )}
                      {hasNewPreemiePermissions() && (
                           <li className={`submenu-item ${menuItemClass('/new-preemie-config')}`}>
                             <Link
                               to="/new-preemie-config"
                               className="full-width-link"
                               onClick={handleMenuItemClick}
                               data-track-id="nav_new_preemie"
                             >
                               <img src={preemieNutritionIcon} alt="new preemie" style={{ width: '16px', height: '16px', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }} />
                               קבלת פג ליחידה
                             </Link>
                           </li>
                         )}
                      {hasSepsisAppPermissions() && (
                           <li className={`submenu-item ${menuItemClass('/sepsis-screening-config')}`}>
                             <Link
                               to="/sepsis-screening-config"
                               className="full-width-link"
                               onClick={handleMenuItemClick}
                             >
                               <FaFlask style={{ marginRight: '10px' }} />
                               סקר ספסיס
                             </Link>
                           </li>
                         )}
                      {hasHematologicalDiseasesPermissions() && (
                           <li className={`submenu-item ${menuItemClass('/hematological-diseases-config')}`}>
                             <Link
                               to="/hematological-diseases-config"
                               className="full-width-link"
                               onClick={handleMenuItemClick}
                               data-track-id="nav_hematological_diseases"
                             >
                               <FaTint style={{ marginRight: '10px' }} />
                               מחלות המטולוגיות
                             </Link>
                           </li>
                         )}
                      {hasPhototherapyPermissions() && (
                        <li className={`submenu-item ${menuItemClass('/phototherapy-config')}`}>
                          <Link
                            to="/phototherapy-config"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                            data-track-id="nav_phototherapy_config"
                          >
                            <FaSun style={{ marginRight: '10px' }} />
                            צהבת ביילוד
                          </Link>
                        </li>
                      )}
                      {hasBili2022Permissions() && (
                        <li className={`submenu-item ${menuItemClass('/bili2022-config')}`}>
                          <Link
                            to="/bili2022-config"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                            data-track-id="nav_bili2022_config"
                          >
                            <FaSun style={{ marginRight: '10px' }} />
                            צהבת ביילוד AAP 2022
                          </Link>
                        </li>
                      )}
                      {hasAntibiogramPermissions() && (
                        <li className={`submenu-item ${menuItemClass('/antibiogram')}`}>
                          <Link
                            to="/antibiogram"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                            data-track-id="nav_antibiogram"
                          >
                            <FaFlask style={{ marginRight: '10px' }} />
                            אנטיביוגרם
                          </Link>
                        </li>
                      )}
                      {user.isSuperAdmin && (
                        <li className={`submenu-item ${menuItemClass('/settings')}`}>
                          <Link
                            to="/settings"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                            data-track-id="nav_settings"
                          >
                            <FaWrench style={{ marginRight: '10px' }} />
                            הגדרות אדמין
                          </Link>
                        </li>
                      )}
                      {user.isSuperAdmin && (
                        <li className={`submenu-item ${menuItemClass('/apps-mgmt')}`}>
                          <Link
                            to="/apps-mgmt"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                            data-track-id="nav_apps_mgmt"
                          >
                            <FaWrench style={{ marginRight: '10px' }} />
                            ניהול אפליקציות
                          </Link>
                        </li>
                      )}
                      {user.isSuperAdmin && (
                        <li className={`submenu-item ${menuItemClass('/stats')}`}>
                          <Link
                            to="/stats"
                            className="full-width-link"
                            onClick={handleMenuItemClick}
                          >
                            <FaChartLine style={{ marginRight: '10px' }} />
                            סטטיסטיקות
                          </Link>
                        </li>
                      )}
                    </ul>
                  </div>
                  <li className="divider"></li>
                </>
              )}
            </ul>
          </nav>
          <div className="logout-container" style={{ color: 'white' }}>
            <nav style={{ marginTop: '0px' }}>
              <ul>
                <li className={menuItemClass('/profile')} >
                  <Link
                    to="/profile"
                    className="logout-button full-width-link"
                    onClick={handleMenuItemClick}
                    data-track-id="nav_profile"
                  >
                    <FaUser style={{ marginLeft: '0px', marginRight: '10px' }} />
                    <div>
                      {user.name}
                      <div style={{ fontSize: '0.8em' }}>{user.email}</div>
                    </div>
                  </Link>
                </li>

                <li className={menuItemClass('logout')}>
                  <button
                    className="logout-button full-width-link"
                    onClick={handleLogout}
                    data-track-id="nav_logout"
                  >
                    <FaSignOutAlt style={{ marginLeft: '0px', marginRight: '10px' }} />
                    התנתק
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Close button for mobile */}
      <button
        className={`close-btn ${isExpanded ? 'visible' : ''}`}
        onClick={() => onToggle(false)}
      >
        <FaTimes />
      </button>
    </div>
  );
};

export default Sidebar;